import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to sync an order document by updating lastUpdated field
 * This triggers pub/sub event and downstream effects
 * @param servicesProviderId - Required services provider ID
 * @param purchasedVehicleId - The purchased vehicle ID
 */
export const syncOrder = async (
	servicesProviderId: string,
	purchasedVehicleId: string,
): Promise<void> => {
	await apiClient.post(ordersEndpoints.sync(servicesProviderId, purchasedVehicleId), {});
};

/**
 * Hook for syncing an order document
 * Takes the purchasedVehicleId directly to avoid confusion with orderId (placedOrderId)
 * Includes cache invalidation for the entire order query
 */
export const useSyncOrder = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation<void, Error, { purchasedVehicleId: string }>({
		mutationFn: async ({ purchasedVehicleId }) => {
			if (!purchasedVehicleId || !servicesProviderId) {
				throw new Error(
					'Unable to sync order without a purchased vehicle ID or services provider ID.',
				);
			}

			return syncOrder(servicesProviderId, purchasedVehicleId);
		},
		onSuccess: (_, { purchasedVehicleId }) => {
			if (!purchasedVehicleId || !servicesProviderId) return;

			// Invalidate entire order using the purchasedVehicleId for cache key
			queryClient.invalidateQueries({
				queryKey: queries.orders.detail(servicesProviderId, purchasedVehicleId).queryKey,
			});
		},
	});
};
